import React from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";

import publicUrls from "../../routes/publicUrls";
import AboutUSs from "./../../images/resources/aboutus.png";

const HowToSell = () => {
  const isMobile = useMediaQuery("(max-width:900px)");

  const steps = [
    {
      number: "01",
      icon: <CameraAltOutlinedIcon />,
      title: "Submit Your Work",
      text: "Upload clear photographs and provide the essential details of the work you would like our team to review.",
    },
    {
      number: "02",
      icon: <DescriptionOutlinedIcon />,
      title: "Our Experts Review",
      text: "Our specialists review the information and assess the work for its suitability for our auctions and selling channels.",
    },
    {
      number: "03",
      icon: <CheckCircleOutlineIcon />,
      title: "Consignment",
      text: "Once accepted, our team guides you through the consignment process and the preparation of your work for sale.",
    },
    {
      number: "04",
      icon: <GavelOutlinedIcon />,
      title: "Auction",
      text: "Your work is presented to collectors through the appropriate León Gallery auction or private-sale channel.",
    },
  ];

  const sellingOptions = [
    {
      icon: <GavelOutlinedIcon />,
      title: "Live Auction",
      text: "Participate in our traditional auction format where works are offered live to collectors.",
      href: "/auctions",
    },
    {
      icon: <PublicOutlinedIcon />,
      title: "Online Auction",
      text: "Reach collectors through our online auction experience.",
      href: "/auctions",
    },
    {
      icon: <ScheduleOutlinedIcon />,
      title: "Timed Auction",
      text: "Offer works through a timed bidding format.",
      href: "/auctions",
    },
    {
      icon: <SellOutlinedIcon />,
      title: "Private Sale",
      text: "Explore a private-sale route for works suited to direct placement.",
      href: publicUrls.privateSales("PAINTING"),
    },
  ];

  return (
    <Box sx={{ bgcolor: "#fff", color: "#111" }}>
      {/* HERO */}
      <Box
        component="section"
        sx={{
          minHeight: isMobile ? 360 : 500,
          backgroundImage: `
            linear-gradient(rgba(0,0,0,.35), rgba(0,0,0,.48)),
            url(${AboutUSs})
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "#fff",
          px: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 4,
              textTransform: "uppercase",
              mb: 2,
              opacity: 0.9,
            }}
          >
            León Gallery
          </Typography>

          <Typography
            sx={{
              fontSize: isMobile ? 42 : 72,
              fontWeight: 400,
              lineHeight: 1,
              mb: 3,
            }}
          >
            How to Sell
          </Typography>

          <Box
            sx={{
              width: 45,
              height: 1,
              bgcolor: "#fff",
              mx: "auto",
              mb: 3,
              opacity: 0.75,
            }}
          />

          <Typography
            sx={{
              fontSize: isMobile ? 12 : 14,
              letterSpacing: 2,
              textTransform: "uppercase",
              opacity: 0.9,
            }}
          >
            Consign with León Gallery
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: isMobile ? 6 : 10 }}>
        {/* INTRO */}
        <Grid container spacing={isMobile ? 4 : 9} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={AboutUSs}
              alt="León Gallery"
              sx={{
                width: "100%",
                display: "block",
                aspectRatio: "4 / 3",
                objectFit: "cover",
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: "#888",
                mb: 2,
              }}
            >
              Your Collection. Our Expertise.
            </Typography>

            <Typography
              sx={{
                fontSize: isMobile ? 32 : 46,
                fontWeight: 400,
                lineHeight: 1.15,
                mb: 3,
              }}
            >
              Consign with confidence.
            </Typography>

            <Typography
              sx={{
                color: "#555",
                fontSize: 15,
                lineHeight: 1.9,
                mb: 4,
                maxWidth: 540,
              }}
            >
              Taking the step to consign a valued possession is an important
              decision. Our team helps guide you through the process, from
              initial submission and specialist review to the appropriate
              selling channel.
            </Typography>

            <Button
              href="/request"
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderColor: "#111",
                color: "#111",
                borderRadius: 0,
                px: 3,
                py: 1.5,
                fontSize: 11,
                letterSpacing: 1.5,
                "&:hover": {
                  bgcolor: "#111",
                  color: "#fff",
                  borderColor: "#111",
                },
              }}
            >
              REQUEST AN ESTIMATE
            </Button>
          </Grid>
        </Grid>

        {/* SHORTCUTS */}
        <Box
          sx={{
            mt: isMobile ? 7 : 10,
            borderTop: "1px solid #ddd",
            borderBottom: "1px solid #ddd",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
          }}
        >
          {[
            ["01", "Submit", "#process"],
            ["02", "Review", "#process"],
            ["03", "Consignment", "#process"],
            ["04", "Selling Options", "#options"],
          ].map(([number, label, href], index) => (
            <Box
              key={label}
              component="a"
              href={href}
              sx={{
                textDecoration: "none",
                color: "#111",
                px: 2,
                py: 2.5,
                borderRight:
                  index !== 3 && !isMobile ? "1px solid #ddd" : "none",
                borderBottom:
                  isMobile && index < 2 ? "1px solid #ddd" : "none",
                "&:hover": { bgcolor: "#f7f7f7" },
                transition: "background .2s ease",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  color: "#999",
                  letterSpacing: 1,
                  mb: 0.5,
                }}
              >
                {number}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* PROCESS */}
        <Box id="process" sx={{ mt: isMobile ? 8 : 12 }}>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: "#888",
              mb: 1.5,
            }}
          >
            Start selling
          </Typography>

          <Typography
            sx={{
              fontSize: isMobile ? 30 : 42,
              fontWeight: 400,
              mb: 6,
            }}
          >
            How it works
          </Typography>

          <Grid container spacing={0}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={step.number}>
                <Box
                  sx={{
                    borderTop: "1px solid #222",
                    borderRight:
                      !isMobile && index !== 3
                        ? "1px solid #ddd"
                        : "none",
                    p: isMobile ? 3 : 3.5,
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        border: "1px solid #222",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {step.icon}
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#999",
                        letterSpacing: 1,
                      }}
                    >
                      {step.number}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 21,
                      fontWeight: 500,
                      mb: 1.5,
                    }}
                  >
                    {step.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.8,
                      color: "#666",
                    }}
                  >
                    {step.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: isMobile ? 8 : 12 }} />

        {/* SELLING OPTIONS */}
        <Box id="options">
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: "#888",
              mb: 1.5,
            }}
          >
            Choose the right route
          </Typography>

          <Typography
            sx={{
              fontSize: isMobile ? 30 : 42,
              fontWeight: 400,
              mb: 5,
            }}
          >
            Selling Options
          </Typography>

          <Grid container spacing={2}>
            {sellingOptions.map((option) => (
              <Grid item xs={12} sm={6} md={3} key={option.title}>
                <Box
                  component="a"
                  href={option.href}
                  sx={{
                    display: "block",
                    height: "100%",
                    border: "1px solid #ddd",
                    color: "#111",
                    textDecoration: "none",
                    p: 3,
                    transition: "all .25s ease",
                    "&:hover": {
                      borderColor: "#111",
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      border: "1px solid #ccc",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    {option.icon}
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    {option.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#666",
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    {option.text}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 10,
                      letterSpacing: 1.5,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    EXPLORE
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FINAL CTA */}
        <Box
          sx={{
            mt: isMobile ? 8 : 12,
            bgcolor: "#111",
            color: "#fff",
            textAlign: "center",
            px: 3,
            py: isMobile ? 6 : 8,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: "#aaa",
              mb: 2,
            }}
          >
            Ready to sell?
          </Typography>

          <Typography
            sx={{
              fontSize: isMobile ? 30 : 44,
              fontWeight: 400,
              mb: 3,
            }}
          >
            Begin your consignment journey.
          </Typography>

          <Typography
            sx={{
              maxWidth: 560,
              mx: "auto",
              color: "#bbb",
              fontSize: 14,
              lineHeight: 1.8,
              mb: 4,
            }}
          >
            Submit your work to our team and let our specialists guide you
            through the next steps.
          </Typography>

          <Button
            href="/request"
            endIcon={<ArrowForwardIcon />}
            sx={{
              color: "#fff",
              border: "1px solid #fff",
              borderRadius: 0,
              px: 3.5,
              py: 1.5,
              fontSize: 11,
              letterSpacing: 1.5,
              "&:hover": {
                bgcolor: "#fff",
                color: "#111",
              },
            }}
          >
            REQUEST AN ESTIMATE
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HowToSell;
